import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ErrorBoundaryProps {
  children: ReactNode;
  // When this value changes (e.g. the route path), the boundary clears its error
  // so navigating away from a crashed page recovers without a full reload.
  resetKey?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

// Catches render errors in its subtree and shows a fallback panel instead of
// letting React unmount the whole app (which leaves a blank white screen).
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Unhandled UI error:", error, errorInfo.componentStack);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  handleRetry = () => {
    this.setState({ error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-[50vh] items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4">
          <AlertTriangle className="h-10 w-10 text-destructive mx-auto" />
          <h2 className="text-lg font-semibold">Something went wrong</h2>
          <p className="text-sm text-muted-foreground">
            This page hit an unexpected error. You can try again, reload the
            page, or use the menu to go to another section.
          </p>
          <div className="flex justify-center gap-2">
            <Button variant="outline" onClick={this.handleRetry}>
              Try again
            </Button>
            <Button onClick={this.handleReload}>Reload page</Button>
          </div>
        </div>
      </div>
    );
  }
}
